import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { validateEmail, validateUsername, validatePassword, sanitizeInput } from '../utils/validation'
import { generateProfilePictureSVG, generateGradientFromUsername } from '../utils/profilePicture'
import './Settings.css'

const Settings = () => {
  const { user, updateUser } = useAuth()
  const navigate = useNavigate()
  const [activeSection, setActiveSection] = useState<'profile' | 'account' | 'password'>('profile')
  const [username, setUsername] = useState(user?.username || '')
  const [email, setEmail] = useState(user?.email || '')
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [loading, setLoading] = useState(false)

  if (!user) {
    navigate('/')
    return null
  }

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    const usernameValidation = validateUsername(username)
    if (!usernameValidation.valid) {
      setError(usernameValidation.error || 'Invalid username')
      return
    }

    if (username !== user.username) {
      // Check if username is available
      setLoading(true)
      const { data: existing } = await supabase
        .from('profiles')
        .select('id')
        .eq('username', sanitizeInput(username).toLowerCase())
        .single()
      if (existing) {
        setError('This username is already taken.')
        setLoading(false)
        return
      }
      setLoading(false)
    }

    await updateUser({ username: sanitizeInput(username).toLowerCase() })
    setSuccess('Profile updated successfully')
  }

  const handleUpdateEmail = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    if (!validateEmail(email)) {
      setError('Please enter a valid email address')
      return
    }

    if (email === user.email) {
      setError('Email is the same as current email')
      return
    }

    setLoading(true)
    const { error: emailError } = await supabase.auth.updateUser({ email: sanitizeInput(email).toLowerCase() })
    setLoading(false)
    if (emailError) {
      setError(emailError.message)
      return
    }
    await updateUser({ email: sanitizeInput(email).toLowerCase() })
    setSuccess('Email updated. Check your inbox to confirm the change.')
  }

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    if (!currentPassword) {
      setError('Current password is required')
      return
    }

    const passwordValidation = validatePassword(newPassword)
    if (!passwordValidation.valid) {
      setError(passwordValidation.error || 'Invalid password')
      return
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match')
      return
    }

    setLoading(true)
    const { error: pwError } = await supabase.auth.updateUser({ password: newPassword })
    setLoading(false)
    if (pwError) {
      setError(pwError.message)
      return
    }
    setSuccess('Password updated successfully')
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
  }

  return (
    <main className="main-content settings-page">
      <div className="settings-container">
        <h1 className="settings-title">settings</h1>

        <div className="settings-layout">
          <div className="settings-sidebar">
            <button
              className={`settings-nav-item ${activeSection === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveSection('profile')}
            >
              <i className="fas fa-user"></i>
              <span>profile</span>
            </button>
            <button
              className={`settings-nav-item ${activeSection === 'account' ? 'active' : ''}`}
              onClick={() => setActiveSection('account')}
            >
              <i className="fas fa-envelope"></i>
              <span>account</span>
            </button>
            <button
              className={`settings-nav-item ${activeSection === 'password' ? 'active' : ''}`}
              onClick={() => setActiveSection('password')}
            >
              <i className="fas fa-lock"></i>
              <span>password</span>
            </button>
          </div>

          <div className="settings-content">
            {activeSection === 'profile' && (
              <div className="settings-section">
                <h2 className="settings-section-title">profile settings</h2>
                <div className="settings-profile-picture">
                  <div
                    className="settings-avatar"
                    style={{ background: generateGradientFromUsername(user.username) }}
                    dangerouslySetInnerHTML={{ __html: generateProfilePictureSVG(user.username, 100) }}
                  />
                  <p className="settings-profile-hint">profile picture is generated from your username</p>
                </div>
                <form onSubmit={handleUpdateProfile}>
                  <div className="settings-form-group">
                    <label className="settings-label" htmlFor="username">
                      username
                    </label>
                    <input
                      type="text"
                      id="username"
                      value={username}
                      onChange={(e) => {
                        const val = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '')
                        setUsername(val)
                      }}
                      className="settings-input"
                      minLength={3}
                      maxLength={20}
                      pattern="[a-z0-9_]+"
                      required
                    />
                    <small className="settings-hint">3-20 characters, letters, numbers, and underscores only</small>
                  </div>
                  {error && <div className="settings-error">{error}</div>}
                  {success && <div className="settings-success">{success}</div>}
                  <button type="submit" className="settings-submit-button" disabled={loading}>
                    {loading ? 'updating...' : 'update profile'}
                  </button>
                </form>
              </div>
            )}

            {activeSection === 'account' && (
              <div className="settings-section">
                <h2 className="settings-section-title">account settings</h2>
                <form onSubmit={handleUpdateEmail}>
                  <div className="settings-form-group">
                    <label className="settings-label" htmlFor="email">
                      email
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(sanitizeInput(e.target.value))}
                      className="settings-input"
                      required
                    />
                  </div>
                  {error && <div className="settings-error">{error}</div>}
                  {success && <div className="settings-success">{success}</div>}
                  <button type="submit" className="settings-submit-button" disabled={loading}>
                    {loading ? 'updating...' : 'update email'}
                  </button>
                </form>
              </div>
            )}

            {activeSection === 'password' && (
              <div className="settings-section">
                <h2 className="settings-section-title">change password</h2>
                <form onSubmit={handleUpdatePassword}>
                  <div className="settings-form-group">
                    <label className="settings-label" htmlFor="current-password">
                      current password
                    </label>
                    <input
                      type="password"
                      id="current-password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="settings-input"
                      required
                    />
                  </div>
                  <div className="settings-form-group">
                    <label className="settings-label" htmlFor="new-password">
                      new password
                    </label>
                    <input
                      type="password"
                      id="new-password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="settings-input"
                      minLength={8}
                      required
                    />
                    <small className="settings-hint">At least 8 characters with letters and numbers</small>
                  </div>
                  <div className="settings-form-group">
                    <label className="settings-label" htmlFor="confirm-password">
                      confirm new password
                    </label>
                    <input
                      type="password"
                      id="confirm-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="settings-input"
                      minLength={8}
                      required
                    />
                  </div>
                  {error && <div className="settings-error">{error}</div>}
                  {success && <div className="settings-success">{success}</div>}
                  <button type="submit" className="settings-submit-button" disabled={loading}>
                    {loading ? 'updating...' : 'update password'}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  )
}

export default Settings

